import React, { useRef, useState } from "react";
import { Box, Collapse, Typography, useTheme } from "@mui/material";
import { ChevronDown, ChevronUp, Zap } from "lucide-react";
import { CustomMarkdown } from "../custom-markdown/custom-markdown";
import {
  getMmkCardBackQuestionStyles,
  getMmkCardFrontHintStyles,
  getMmkCardFrontQuestionStyles,
  getMmkCardQuestionLabelStyles,
  getMmkDescTextStyles,
  getMmkFullAnswerTitleStyles,
  getMmkFullAnswerToggleStyles,
  getMmkMarkdownEmphasisColor,
  getMmkQuickAnswerBoxStyles,
  getMmkQuickAnswerHeaderStyles,
  getMmkQuickAnswerTextStyles,
  mmkCardFrontBodyStyles,
  type MmkCardVariant,
} from "./styles";

const FRONT_HINT = "Think first, then reveal the approved response.";

function scrollContainerToElement(el: HTMLElement) {
  let parent: HTMLElement | null = el.parentElement;
  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    if (overflowY === "auto" || overflowY === "scroll") {
      const parentRect = parent.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const nextTop = elRect.top - parentRect.top + parent.scrollTop - 8;
      parent.scrollTo({ top: Math.max(0, nextTop), behavior: "smooth" });
      return;
    }
    parent = parent.parentElement;
  }
  el.scrollIntoView({ behavior: "smooth", block: "nearest" });
}

interface MmkCardFrontContentProps {
  question: string;
  fontSize?: string | { xs?: string; sm?: string };
  variant?: MmkCardVariant;
}

export const MmkCardFrontContent: React.FC<MmkCardFrontContentProps> = ({
  question,
  fontSize,
  variant = "default",
}) => (
  <Box sx={mmkCardFrontBodyStyles}>
    <Box
      sx={(theme) => ({
        ...(
          getMmkCardFrontQuestionStyles() as (t: typeof theme) => object
        )(theme),
        ...(fontSize != null ? { fontSize } : null),
        width: "100%",
        "& strong, & b": {
          fontWeight: 800,
          fontSize: "1.15em",
          color: getMmkMarkdownEmphasisColor(theme, variant),
        },
        "& p": { m: 0 },
        "& ul, & ol": { m: 0, pl: 2.5, textAlign: "left" },
        "& li": { mb: 0.5 },
      })}
    >
      <CustomMarkdown skipHtml>{question}</CustomMarkdown>
    </Box>
    <Typography component="span" sx={getMmkCardFrontHintStyles()}>
      {FRONT_HINT}
    </Typography>
  </Box>
);

interface MmkCardBackContentProps {
  question: string;
  answer: string;
  shortAnswer?: string | null;
  stopPropagationOnToggle?: boolean;
  variant?: MmkCardVariant;
}

export const MmkCardBackContent: React.FC<MmkCardBackContentProps> = ({
  question,
  answer,
  shortAnswer,
  stopPropagationOnToggle = true,
  variant = "default",
}) => {
  const theme = useTheme();
  const [fullAnswerOpen, setFullAnswerOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const trimmedShort = shortAnswer?.trim() || "";
  const hasShortAnswer = trimmedShort.length > 0;
  const isDark = theme.palette.mode === "dark";

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Typography component="div" sx={getMmkCardQuestionLabelStyles()}>
        Question
      </Typography>
      <Box
        sx={(t) => ({
          ...(getMmkCardBackQuestionStyles() as (x: unknown) => object)(t),
          "& strong, & b": {
            fontWeight: 800,
            fontSize: "1.15em",
            color: getMmkMarkdownEmphasisColor(t, variant),
          },
          "& p": { m: 0 },
          "& ul, & ol": { m: 0, pl: 2.5 },
          "& li": { mb: 0.5 },
        })}
      >
        <CustomMarkdown skipHtml>{question}</CustomMarkdown>
      </Box>

      {hasShortAnswer && (
        <Box sx={getMmkQuickAnswerBoxStyles()}>
          <Box sx={getMmkQuickAnswerHeaderStyles()}>
            <Zap size={14} fill="currentColor" strokeWidth={0} />
            Quick answer
          </Box>
          <Box
            sx={(t) => ({
              ...(getMmkQuickAnswerTextStyles() as (x: unknown) => object)(t),
              "& strong, & b": { fontWeight: 700 },
              "& p": { m: 0 },
              "& ul, & ol": { m: 0, pl: 2.5 },
              "& li": { mb: 0.5 },
            })}
          >
            <CustomMarkdown skipHtml>{trimmedShort}</CustomMarkdown>
          </Box>
        </Box>
      )}

      <Box
        component="button"
        type="button"
        ref={toggleRef}
        sx={getMmkFullAnswerToggleStyles()}
        onClick={(e) => {
          if (stopPropagationOnToggle) e.stopPropagation();
          setFullAnswerOpen((open) => !open);
        }}
        aria-expanded={fullAnswerOpen}
      >
        {fullAnswerOpen ? (
          <ChevronUp size={16} color={isDark ? "#60A5FA" : "#2563EB"} />
        ) : (
          <ChevronDown size={16} color={isDark ? "#60A5FA" : "#2563EB"} />
        )}
        <Typography component="span" sx={getMmkFullAnswerTitleStyles()}>
          Full approved answer
        </Typography>
      </Box>

      <Collapse
        in={fullAnswerOpen}
        timeout="auto"
        unmountOnExit={false}
        onEntered={() => {
          if (toggleRef.current) scrollContainerToElement(toggleRef.current);
        }}
      >
        <Box
          sx={(t) => ({
            ...(getMmkDescTextStyles() as (x: unknown) => object)(t),
            "& strong, & b": { fontWeight: 700 },
            "& p": { m: 0, mb: 0.75, "&:last-child": { mb: 0 } },
            "& ul, & ol": {
              m: 0,
              pl: 0,
              listStyle: "none",
            },
            "& li": {
              position: "relative",
              mb: 0.75,
              pl: 2.25,
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: "0.55em",
                width: 7,
                height: 7,
                borderRadius: "50%",
                backgroundColor: "#06B6D4",
              },
            },
          })}
        >
          <CustomMarkdown skipHtml>{answer}</CustomMarkdown>
        </Box>
      </Collapse>
    </Box>
  );
};
